import { Check, ChevronDown, Search } from 'lucide-react'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

export type SelectValue = string | number

export interface SelectOption<T extends SelectValue> {
  value: T
  label: string
  /** Leading adornment: a flag, an emoji or an icon. */
  leading?: ReactNode
  /** Secondary line under the label. */
  hint?: string
  disabled?: boolean
  /** Extra text the search box matches, e.g. a country code. */
  keywords?: string
}

export interface SelectGroup<T extends SelectValue> {
  label: string
  options: SelectOption<T>[]
}

export type SelectItem<T extends SelectValue> = SelectOption<T> | SelectGroup<T>

interface SelectProps<T extends SelectValue> {
  value: T | null | undefined
  onChange: (value: T) => void
  options: SelectItem<T>[]
  placeholder?: string
  /**
   * 'field': a Material outlined field (border, surface, focus ring).
   * 'inline': the bare value with a chevron, for a list row that already
   * draws its own frame.
   */
  variant?: 'field' | 'inline'
  size?: 'sm' | 'md'
  /** A search box at the top of the menu, for long lists such as countries. */
  searchable?: boolean
  /** The search box's placeholder (and label); defaults to the generic "Search". */
  searchPlaceholder?: string
  disabled?: boolean
  invalid?: boolean
  /** Stretches to the container (the default); false keeps the trigger at its content width. */
  fullWidth?: boolean
  id?: string
  className?: string
  /** Which edge of the trigger the menu lines up with. */
  align?: 'start' | 'end'
  'aria-label'?: string
  /** Renders the closed field's content; defaults to the option's leading + label. */
  renderValue?: (option: SelectOption<T>) => ReactNode
}

const MENU_MAX_HEIGHT = 320
const MENU_MIN_WIDTH = 208
const VIEWPORT_GUTTER = 8
const MENU_GAP = 4

type GroupRow = { kind: 'group'; key: string; label: string; first: boolean }
type OptionRow<T extends SelectValue> = {
  kind: 'option'
  key: string
  option: SelectOption<T>
  index: number
}
type Row<T extends SelectValue> = GroupRow | OptionRow<T>

function isSelectGroup<T extends SelectValue>(item: SelectItem<T>): item is SelectGroup<T> {
  return 'options' in item
}

function isOptionRow<T extends SelectValue>(row: Row<T>): row is OptionRow<T> {
  return row.kind === 'option'
}

/** Diacritics-insensitive, case-insensitive haystack for the search box. */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/**
 * The menu rows after the search filter: group headers only survive with
 * at least one matching option under them. Option rows are numbered in
 * display order, which is what the keyboard walks.
 */
function buildRows<T extends SelectValue>(items: SelectItem<T>[], query: string): Row<T>[] {
  const needle = fold(query.trim())
  const matches = (option: SelectOption<T>) =>
    needle === '' || fold(`${option.label} ${option.keywords ?? ''}`).includes(needle)
  const rows: Row<T>[] = []
  let index = 0
  let groups = 0
  const push = (option: SelectOption<T>) => {
    // The position joins the key: one value may sit in two groups (a
    // "Recently used" group repeats options of the catalog below).
    rows.push({ kind: 'option', key: `o-${index}-${String(option.value)}`, option, index })
    index += 1
  }
  for (const item of items) {
    if (isSelectGroup(item)) {
      const visible = item.options.filter(matches)
      if (visible.length === 0) continue
      rows.push({ kind: 'group', key: `g-${item.label}`, label: item.label, first: groups === 0 })
      groups += 1
      visible.forEach(push)
    } else if (matches(item)) {
      push(item)
    }
  }
  return rows
}

/** The next enabled option after `from` walking `step`, wrapping; -1 when none. */
function nextEnabled<T extends SelectValue>(
  list: OptionRow<T>[],
  from: number,
  step: 1 | -1,
): number {
  const count = list.length
  let index = from
  for (let i = 0; i < count; i += 1) {
    index = (index + step + count) % count
    if (!list[index]!.option.disabled) return index
  }
  return -1
}

/**
 * Where the menu goes: under the trigger, or above it when the room below
 * is short and there is more above. Fixed coordinates, so the menu is never
 * clipped by a scrolling panel or a modal.
 */
function placeMenu(
  anchor: DOMRect,
  align: 'start' | 'end',
  fill: boolean,
): { style: CSSProperties; above: boolean } {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const width = Math.min(
    fill ? anchor.width : Math.max(anchor.width, MENU_MIN_WIDTH),
    vw - VIEWPORT_GUTTER * 2,
  )
  const rawLeft = align === 'end' ? anchor.right - width : anchor.left
  const left = Math.max(VIEWPORT_GUTTER, Math.min(rawLeft, vw - width - VIEWPORT_GUTTER))
  const roomBelow = vh - anchor.bottom - VIEWPORT_GUTTER - MENU_GAP
  const roomAbove = anchor.top - VIEWPORT_GUTTER - MENU_GAP
  const above = roomBelow < Math.min(MENU_MAX_HEIGHT, 240) && roomAbove > roomBelow
  const maxHeight = Math.max(140, Math.min(MENU_MAX_HEIGHT, above ? roomAbove : roomBelow))
  const style: CSSProperties = above
    ? { left, width, maxHeight, bottom: vh - anchor.top + MENU_GAP }
    : { left, width, maxHeight, top: anchor.bottom + MENU_GAP }
  return { style, above }
}

/**
 * The app's dropdown: a Material-style select with a portal menu. Surface
 * and text come from the theme tokens, so the list reads in every theme
 * (the native popup did not). Keyboard: arrows, Home/End, Enter, Escape,
 * type-ahead; an optional search box for long lists. Options can carry a
 * flag or icon and can be grouped.
 */
export function Select<T extends SelectValue>({
  value,
  onChange,
  options,
  placeholder,
  variant = 'field',
  size = 'md',
  searchable = false,
  searchPlaceholder,
  disabled = false,
  invalid = false,
  fullWidth = true,
  id,
  className = '',
  align = 'start',
  'aria-label': ariaLabel,
  renderValue,
}: SelectProps<T>) {
  const { t } = useTranslation('common')
  const reactId = useId()
  const listId = `${reactId}-list`
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const typeahead = useRef({ buffer: '', at: 0 })
  /** Set by a close that should hand the focus back to the trigger. */
  const refocusOnClose = useRef(false)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const [placement, setPlacement] = useState<{ style: CSSProperties; above: boolean } | null>(
    null,
  )

  const fill = variant === 'field' && fullWidth
  const flat = useMemo(
    () => options.flatMap((item) => (isSelectGroup(item) ? item.options : [item])),
    [options],
  )
  const selected = useMemo(
    () => (value == null ? undefined : flat.find((option) => Object.is(option.value, value))),
    [flat, value],
  )
  const rows = useMemo(() => buildRows(options, query), [options, query])
  const visibleOptions = useMemo(() => rows.filter(isOptionRow), [rows])

  const openMenu = () => {
    if (disabled) return
    const anchor = triggerRef.current?.getBoundingClientRect()
    if (!anchor) return
    setPlacement(placeMenu(anchor, align, fill))
    // Highlight the current value, else the first option.
    const initial = buildRows(options, '').filter(isOptionRow)
    const current = initial.findIndex((row) => selected != null && row.option === selected)
    setActiveIndex(current >= 0 ? current : nextEnabled(initial, -1, 1))
    setQuery('')
    setOpen(true)
  }

  const close = (refocus: boolean) => {
    refocusOnClose.current = refocus
    setOpen(false)
    setQuery('')
    setActiveIndex(-1)
  }

  const choose = (option: SelectOption<T>) => {
    if (option.disabled) return
    onChange(option.value)
    close(true)
  }

  const search = (next: string) => {
    setQuery(next)
    // A narrower list restarts the highlight at its first hit.
    setActiveIndex(nextEnabled(buildRows(options, next).filter(isOptionRow), -1, 1))
  }

  // Opening moves the focus into the menu; a closing that asked for it
  // hands the focus back to the trigger.
  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => {
        ;(searchable ? searchRef.current : listRef.current)?.focus({ preventScroll: true })
      })
      return () => cancelAnimationFrame(frame)
    }
    if (refocusOnClose.current) {
      refocusOnClose.current = false
      triggerRef.current?.focus()
    }
    return undefined
  }, [open, searchable])

  // Follow the trigger while the page scrolls or the window resizes.
  useEffect(() => {
    if (!open) return
    const reposition = () => {
      const anchor = triggerRef.current?.getBoundingClientRect()
      if (anchor) setPlacement(placeMenu(anchor, align, fill))
    }
    window.addEventListener('resize', reposition)
    document.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      document.removeEventListener('scroll', reposition, true)
    }
  }, [open, align, fill])

  // Click outside closes without stealing the focus back.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
      setQuery('')
      setActiveIndex(-1)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  // Keep the highlighted row in view.
  useEffect(() => {
    if (!open || activeIndex < 0) return
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const moveActive = (step: 1 | -1) =>
    setActiveIndex((index) => nextEnabled(visibleOptions, index, step))

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault()
      if (!open) openMenu()
    }
  }

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveActive(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        moveActive(-1)
        return
      case 'Home':
        event.preventDefault()
        setActiveIndex(nextEnabled(visibleOptions, -1, 1))
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(nextEnabled(visibleOptions, 0, -1))
        return
      case 'Enter': {
        event.preventDefault()
        const row = visibleOptions[activeIndex]
        if (row) choose(row.option)
        return
      }
      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        close(true)
        return
      case 'Tab':
        close(false)
        return
      default:
        break
    }
    // Type-ahead on a plain list: letters jump to the next matching label.
    if (searchable || event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) {
      return
    }
    const now = event.timeStamp
    const state = typeahead.current
    state.buffer = now - state.at < 600 ? state.buffer + event.key : event.key
    state.at = now
    const needle = fold(state.buffer)
    const start = state.buffer.length === 1 ? activeIndex : activeIndex - 1
    const count = visibleOptions.length
    for (let i = 1; i <= count; i += 1) {
      const index = (start + i + count) % count
      const row = visibleOptions[index]!
      if (!row.option.disabled && fold(row.option.label).startsWith(needle)) {
        setActiveIndex(index)
        return
      }
    }
  }

  const isField = variant === 'field'
  const triggerClass = [
    'group flex min-w-0 items-center gap-2 text-left transition-[border-color,box-shadow,color,background-color] duration-150 focus:outline-none',
    fullWidth ? 'w-full' : 'w-auto',
    isField
      ? [
          'rounded-xl border bg-surface',
          size === 'sm' ? 'min-h-9 px-3 py-1.5 text-xs' : 'min-h-11 px-3.5 py-2.5 text-sm',
          invalid
            ? 'border-danger-solid'
            : open
              ? 'border-primary ring-2 ring-primary/25'
              : 'border-border',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-fg-subtle',
        ].join(' ')
      : [
          'rounded-md',
          size === 'sm' ? 'text-xs' : 'text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          'focus-visible:ring-primary/30 focus-visible:ring-2',
        ].join(' '),
    selected ? 'text-fg' : 'text-fg-subtle',
    className,
  ].join(' ')

  const activeRow = visibleOptions[activeIndex]
  const activeDescendant = activeRow ? `${reactId}-opt-${activeRow.index}` : undefined

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        className={triggerClass}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {selected ? (
            (renderValue?.(selected) ?? (
              <>
                {selected.leading != null && (
                  <span aria-hidden className="flex shrink-0 items-center leading-none">
                    {selected.leading}
                  </span>
                )}
                <span className="truncate">{selected.label}</span>
              </>
            ))
          ) : (
            <span className="truncate">{placeholder ?? ' '}</span>
          )}
        </span>
        <ChevronDown
          aria-hidden
          className={`text-fg-subtle shrink-0 transition-transform duration-150 ${
            size === 'sm' ? 'size-3.5' : 'size-4'
          } ${open ? 'text-primary rotate-180' : ''}`}
        />
      </button>

      {open &&
        placement &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', ...placement.style }}
            className={`border-border bg-surface text-fg z-[70] flex flex-col overflow-hidden rounded-xl border shadow-[0_18px_44px_-14px_rgba(0,0,0,0.6)] ${
              placement.above ? 'select-menu-above' : 'select-menu-below'
            }`}
            onKeyDown={onMenuKeyDown}
          >
            {searchable && (
              <div className="border-border flex items-center gap-2 border-b px-3 py-2">
                <Search aria-hidden className="text-fg-subtle size-4 shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(event) => search(event.target.value)}
                  placeholder={searchPlaceholder ?? t('select.search')}
                  aria-label={searchPlaceholder ?? t('select.search')}
                  aria-controls={listId}
                  aria-activedescendant={activeDescendant}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="text-fg placeholder:text-fg-subtle w-full bg-transparent text-sm focus:outline-none"
                />
              </div>
            )}
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              tabIndex={-1}
              aria-label={ariaLabel}
              aria-activedescendant={searchable ? undefined : activeDescendant}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1.5 focus:outline-none"
            >
              {rows.length === 0 ? (
                <li className="text-fg-subtle px-3.5 py-6 text-center text-sm">
                  {t('select.noResults')}
                </li>
              ) : (
                rows.map((row) =>
                  row.kind === 'group' ? (
                    <li
                      key={row.key}
                      role="presentation"
                      className={`text-fg-subtle px-3.5 pt-2.5 pb-1 text-[11px] font-semibold tracking-wider uppercase ${
                        row.first ? '' : 'border-border mt-1 border-t'
                      }`}
                    >
                      {row.label}
                    </li>
                  ) : (
                    <li
                      key={row.key}
                      id={`${reactId}-opt-${row.index}`}
                      role="option"
                      data-index={row.index}
                      aria-selected={row.option === selected}
                      aria-disabled={row.option.disabled || undefined}
                      onPointerMove={() => {
                        if (!row.option.disabled && activeIndex !== row.index) {
                          setActiveIndex(row.index)
                        }
                      }}
                      onClick={() => choose(row.option)}
                      className={[
                        'mx-1.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                        row.option.disabled
                          ? 'text-fg-subtle cursor-default opacity-50'
                          : 'cursor-pointer',
                        row.option === selected
                          ? 'bg-primary-soft text-primary font-semibold'
                          : activeIndex === row.index
                            ? 'bg-surface-hover text-fg'
                            : 'text-fg',
                      ].join(' ')}
                    >
                      {row.option.leading != null && (
                        <span
                          aria-hidden
                          className="flex w-5 shrink-0 items-center justify-center text-base leading-none"
                        >
                          {row.option.leading}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{row.option.label}</span>
                        {row.option.hint && (
                          <span className="text-fg-subtle block truncate text-xs font-normal">
                            {row.option.hint}
                          </span>
                        )}
                      </span>
                      {row.option === selected && (
                        <Check aria-hidden className="text-primary size-4 shrink-0" />
                      )}
                    </li>
                  ),
                )
              )}
            </ul>
          </div>,
          document.body,
        )}
    </>
  )
}
